export const API_DOCUMENTATION_GROUPS = [
    {
        id: 'content',
        label: 'Content',
        description: 'Read and search release-specific documentation content.',
    },
    {
        id: 'pdf',
        label: 'PDF',
        description: 'Resolve ordered article selections and create downloadable PDFs.',
    },
    {
        id: 'llm',
        label: 'LLM',
        description: 'Start authenticated LLM processing and retrieve the generated Markdown answer.',
    },
    {
        id: 'jobs',
        label: 'Jobs',
        description: 'Monitor and stop individual jobs or job groups.',
    },
];

const releaseQueryParameters = ({ language, version, product }, versionOptions) => [
    {
        name: 'lang',
        in: 'query',
        type: 'string',
        required: true,
        default: language,
        description: 'ISO 639-1 language code.',
    },
    {
        name: 'ver',
        in: 'query',
        type: 'string',
        required: true,
        default: version,
        options: versionOptions,
        description: 'Documentation version. The newest public version is selected initially.',
    },
    {
        name: 'product',
        in: 'query',
        type: 'string',
        required: true,
        default: product,
        description: 'Product identifier, for example supervisor, addin, or com.',
    },
];

const releaseBody = ({ language, version, product }) => ({
    language,
    version,
    product,
});

const SAMPLE_TOC = [
    {
        _id: '14679',
        title: 'Engineering Studio',
        language: 'en',
        topic_id: '14679',
        children: [],
    },
];

const SAMPLE_ARTICLE = {
    _id: '14679',
    title: 'Guide & overview for zenon Help',
    language: 'en',
    topic_id: '14679',
    body: '<div><x-heading>Guide &amp; overview</x-heading><p>...</p></div>',
    metadata: { keywords: ['Guide & overview for zenon Help'] },
    resources: [],
};

const SAMPLE_SEARCH = [
    {
        _id: 'c_00525dedd6b3d8ada88cb7868eb028e2',
        title: 'Create',
        topic_id: 'c_00525dedd6b3d8ada88cb7868eb028e2',
        body: 'Create Summary - Namespace: Scada.AddIn.Contracts ...',
        term_suggestion: null,
    },
];

const SAMPLE_SELECTION = {
    count_topics: 2,
    count_articles: 2,
    topic_ids: ['14679', '14680'],
    toc_node_ids: ['14679', '14680'],
    article_ids: ['14679', '14680'],
    missing_topic_ids: [],
};

export const SAMPLE_JOB = {
    jobId: '3a4f...',
    groupId: null,
    status: 'RUNNING',
    progress: 42.5,
    message: 'Rendering article 17/40',
    errors: [],
};

function pathParameter(name, description, defaultValue = '') {
    return {
        name,
        in: 'path',
        type: 'string',
        required: true,
        default: defaultValue,
        description,
    };
}

export function createApiEndpointGroups({ release, versionOptions = [] }) {
    const releaseQuery = releaseQueryParameters(release, versionOptions);
    const releasePayload = releaseBody(release);

    return {
        content: [
            {
                id: 'toc',
                title: 'Get the table of contents',
                summary: 'Returns the complete nested table of contents for one release.',
                method: 'GET',
                path: '/toc',
                access: 'Public',
                parameters: releaseQuery,
                exampleResponse: SAMPLE_TOC,
            },
            {
                id: 'article',
                title: 'Get an article by ID',
                summary: 'Returns article metadata and the HTML body for one release.',
                method: 'GET',
                path: '/article/{article_id}',
                access: 'Public',
                parameters: [pathParameter('article_id', 'Article ID.', '14679'), ...releaseQuery],
                exampleResponse: SAMPLE_ARTICLE,
                notes: [
                    'Treat the body field as HTML and sanitize it before rendering in another application.',
                    'Relative image paths must be resolved against the release-specific image bucket described below.',
                ],
            },
            {
                id: 'search',
                title: 'Search articles',
                summary: 'Returns ranked full-text search results for the selected release.',
                method: 'GET',
                path: '/search',
                access: 'Public · search feature',
                parameters: [
                    { name: 'q', in: 'query', type: 'string', required: true, default: 'Create', description: 'Search query.' },
                    { name: 'size', in: 'query', type: 'integer', required: false, default: 30, description: 'Results per page, from 1 to 100.' },
                    { name: 'page', in: 'query', type: 'integer', required: false, default: 1, description: 'One-based result page.' },
                    ...releaseQuery,
                ],
                exampleResponse: SAMPLE_SEARCH,
            },
            {
                id: 'subtree-article-ids',
                title: 'Resolve a TOC subtree',
                summary: 'Expands one TOC node into ordered topic, TOC-node, and article IDs.',
                method: 'GET',
                path: '/toc/subtree-article-ids',
                access: 'Public',
                parameters: [
                    { name: 'topic_id', in: 'query', type: 'string', required: false, default: '14679', description: 'Subtree root topic ID. Supply this or node_id.' },
                    { name: 'node_id', in: 'query', type: 'string', required: false, default: '', description: 'Subtree root TOC-node ID. Supply this or topic_id.' },
                    { name: 'by', in: 'query', type: 'string', required: false, default: '', options: ['', 'topic_id', '_id'], description: 'Optional lookup mode; inferred when omitted.' },
                    { name: 'include_self', in: 'query', type: 'boolean', required: false, default: true, options: ['true', 'false'], description: 'Include the selected root in the result.' },
                    ...releaseQuery,
                ],
                exampleResponse: SAMPLE_SELECTION,
            },
            {
                id: 'all-article-ids',
                title: 'Resolve ordered article IDs',
                summary: 'Returns the complete TOC selection, or the ordered union below selected roots.',
                method: 'GET',
                path: '/toc/all-article-ids',
                access: 'Public',
                parameters: [
                    { name: 'node_ids', in: 'query', type: 'CSV string', required: false, default: '', description: 'Optional comma-separated TOC-node roots.' },
                    { name: 'topic_ids', in: 'query', type: 'CSV string', required: false, default: '', description: 'Optional comma-separated topic roots.' },
                    { name: 'by', in: 'query', type: 'string', required: false, default: '', options: ['', 'topic_id', '_id'], description: 'Optional lookup mode.' },
                    { name: 'include_self', in: 'query', type: 'boolean', required: false, default: true, options: ['true', 'false'], description: 'Include each selected root.' },
                    ...releaseQuery,
                ],
                exampleResponse: SAMPLE_SELECTION,
                notes: ['The legacy alias /toc/allarticleids remains available; new clients should use /toc/all-article-ids.'],
            },
        ],
        pdf: [
            {
                id: 'create-pdf',
                title: 'Create a PDF',
                summary: 'Starts PDF generation for up to 200 ordered article IDs.',
                method: 'POST',
                path: '/print',
                access: 'Public',
                requestBody: {
                    article_ids: ['14679', '14680'],
                    ...releasePayload,
                },
                exampleResponse: {
                    pdfUrl: 'https://api.example.test/print/files/<token>',
                    jobId: '3a4f...',
                },
                responseStatus: 200,
                notes: [
                    'Poll GET /jobs/{job_id} until the status is COMPLETED, FAILED, or STOPPED.',
                    'Open pdfUrl only after the job completes. Generated files are temporary and may return 404 after expiry or restart.',
                ],
            },
            {
                id: 'download-pdf',
                title: 'Download a completed PDF',
                summary: 'Streams the generated file as application/pdf.',
                method: 'GET',
                path: '/print/files/{token}',
                access: 'Public',
                parameters: [pathParameter('token', 'Token from the pdfUrl returned by POST /print.')],
                responseType: 'blob',
                notes: ['Returns 404 when the generated file has expired, the backend restarted, or the token is unknown.'],
            },
        ],
        llm: [
            {
                id: 'create-llm-answer',
                title: 'Create an LLM answer',
                summary: 'Starts LLM processing for up to 200 ordered article IDs.',
                method: 'POST',
                path: '/llm',
                access: 'Docs-Admin · LLM feature',
                requiresAuth: true,
                requestBody: {
                    prompt: 'Summarize the selected articles.',
                    article_ids: ['14679', '14680'],
                    ...releasePayload,
                },
                exampleResponse: {
                    jobId: '3a4f...',
                    answerUrl: 'https://api.example.test/llm/answers/<token>',
                },
                responseStatus: 202,
                notes: [
                    'Poll GET /jobs/{job_id} until the job reaches a terminal state.',
                    'Fetch answerUrl after completion. The answer is temporary and may return 404 after expiry or restart.',
                ],
            },
            {
                id: 'get-llm-answer',
                title: 'Get a completed LLM answer',
                summary: 'Returns the generated Markdown after its job completes.',
                method: 'GET',
                path: '/llm/answers/{token}',
                access: 'Docs-Admin · LLM feature',
                requiresAuth: true,
                parameters: [pathParameter('token', 'Token from the answerUrl returned by POST /llm.')],
                exampleResponse: { answer: '# Summary\n\nGenerated Markdown...' },
            },
        ],
        jobs: [
            {
                id: 'get-job',
                title: 'Get job status',
                summary: 'Returns the authoritative state and progress of one asynchronous job.',
                method: 'GET',
                path: '/jobs/{job_id}',
                access: 'Public',
                parameters: [pathParameter('job_id', 'Job ID returned by a job-starting endpoint.')],
                exampleResponse: SAMPLE_JOB,
                notes: ['Terminal statuses are COMPLETED, FAILED, and STOPPED. A reaped or unknown job returns 404.'],
            },
            {
                id: 'stop-job',
                title: 'Stop a job',
                summary: 'Requests cooperative cancellation of one job.',
                method: 'POST',
                path: '/jobs/{job_id}/stop',
                access: 'Docs-Admin or API key',
                requiresAuth: true,
                parameters: [pathParameter('job_id', 'Job ID to stop.')],
                exampleResponse: { status: 'stopping' },
                responseStatus: 200,
            },
            {
                id: 'get-job-group',
                title: 'Get a job group',
                summary: 'Returns a group ID and its member job IDs.',
                method: 'GET',
                path: '/jobs/groups/{group_id}',
                access: 'Public',
                parameters: [pathParameter('group_id', 'Group ID returned by a grouped operation.')],
                exampleResponse: { groupId: '9b2e...', jobs: ['3a4f...', '7c1d...'] },
            },
            {
                id: 'get-job-group-info',
                title: 'Get job group details',
                summary: 'Returns the current state of every member in a job group.',
                method: 'GET',
                path: '/jobs/groups/{group_id}/info',
                access: 'Public',
                parameters: [pathParameter('group_id', 'Group ID returned by a grouped operation.')],
                exampleResponse: { groupId: '9b2e...', jobs: [SAMPLE_JOB] },
                notes: ['Stop polling when every member status is COMPLETED, FAILED, or STOPPED.'],
            },
            {
                id: 'stop-job-group',
                title: 'Stop a job group',
                summary: 'Requests cooperative cancellation of all jobs in a group.',
                method: 'POST',
                path: '/jobs/groups/{group_id}/stop',
                access: 'Docs-Admin or API key',
                requiresAuth: true,
                parameters: [pathParameter('group_id', 'Group ID to stop.')],
                exampleResponse: { status: 'stopping' },
                responseStatus: 200,
            },
        ],
    };
}
